import { forwardRef } from "react";
import type { HTMLAttributes, MouseEvent } from "react";
import { motion, useReducedMotion, useSpring } from "framer-motion";
import clsx from "clsx";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
  tilt?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive = false, tilt = false, children, onMouseMove, onMouseLeave, ...props }, ref) => {
    const reducedMotion = useReducedMotion();
    const enableTilt = interactive && tilt && !reducedMotion;

    const rotateX = useSpring(0, { stiffness: 250, damping: 22 });
    const rotateY = useSpring(0, { stiffness: 250, damping: 22 });

    const classes = clsx(
      "rounded-2xl border border-border bg-surface-raised shadow-soft",
      interactive && "cursor-pointer",
      className
    );

    if (!interactive) {
      return (
        <div ref={ref} className={classes} {...(props as HTMLAttributes<HTMLDivElement>)}>
          {children}
        </div>
      );
    }

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
      if (enableTilt) {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        rotateY.set(px * 8);
        rotateX.set(-py * 8);
      }
      onMouseMove?.(e);
    };

    const handleMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
      if (enableTilt) {
        rotateX.set(0);
        rotateY.set(0);
      }
      onMouseLeave?.(e);
    };

    return (
      <motion.div
        ref={ref}
        style={enableTilt ? { rotateX, rotateY, transformPerspective: 800 } : undefined}
        whileHover={{ y: -6, boxShadow: "0 24px 48px -16px rgba(0,0,0,0.22)" }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={classes}
        {...(props as any)}
      >
        {children}
      </motion.div>
    );
  }
);
Card.displayName = "Card";
